import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { adminRewards, adminUsers, type AdminReferral, type AdminCoinTx, type UserAdmin } from '../../api/admin';
import { useInfiniteList, rowCursor } from '../../hooks/useInfiniteList';
import { Gift, Coins, Search } from 'lucide-react';

type Tab = 'referrals' | 'coins';

export default function RewardsAdminPage() {
  const { isDark } = useTheme();
  const [tab, setTab] = useState<Tab>('referrals');

  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<UserAdmin | null>(null);
  const [coinsSummary, setCoinsSummary] = useState<{ user: { email: string; first_name: string }; balance: number } | null>(null);
  const [adjustKey, setAdjustKey] = useState(0);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    adminUsers.list().then(setUsers).catch(() => {});
  }, []);

  // Referrals + the selected user's coin ledger both paginate (100/page, load on scroll).
  const referralsList = useInfiniteList<AdminReferral>(
    async (cursor) => {
      const r = await adminRewards.referrals(cursor);
      return { items: r.items, hasMore: r.has_more };
    },
    (r) => rowCursor(r.created_at, r.id),
    'referrals',
  );
  const ledger = useInfiniteList<AdminCoinTx>(
    async (cursor) => {
      const r = await adminRewards.userCoins(selected!.id, cursor);
      if (!cursor) setCoinsSummary({ user: r.user, balance: r.balance });
      return { items: r.history, hasMore: r.has_more };
    },
    (h) => rowCursor(h.created_at, h.id),
    selected ? `${selected.id}:${adjustKey}` : null,
    !!selected,
  );

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter((u) => u.email.toLowerCase().includes(q) || `${u.first_name} ${u.last_name}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, users]);

  const pickUser = (u: UserAdmin) => {
    setSelected(u);
    setSearch('');
    setMsg(''); setErr('');
    setCoinsSummary(null);
  };

  const submitAdjust = async () => {
    setMsg(''); setErr('');
    const amt = parseInt(amount, 10);
    if (!selected || !amt) { setErr('Enter a non-zero amount'); return; }
    try {
      await adminRewards.adjust(selected.id, amt, note.trim() || undefined);
      setAmount(''); setNote('');
      setMsg(`Adjusted by ${amt > 0 ? '+' : ''}${amt} coins.`);
      setAdjustKey((k) => k + 1);
    } catch (e: any) {
      setErr(e?.message || 'Adjustment failed');
    }
  };

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const statusPill = (s: string) =>
    s === 'qualified' ? 'bg-emerald-500/15 text-emerald-500'
    : s === 'reversed' ? 'bg-red-500/15 text-red-500'
    : 'bg-amber-500/15 text-amber-500';

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Rewards</h1>
        <p className={`text-sm ${sub}`}>Referrals and ZS Coin balances.</p>
      </div>

      <div className="flex gap-2">
        {([['referrals', 'Referrals', Gift], ['coins', 'Coin Balances', Coins]] as const).map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              tab === k ? 'bg-emerald-500 border-emerald-500 text-white' : `${card} ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'referrals' && (
        <div onScroll={referralsList.onScroll} className={`rounded-xl border overflow-auto max-h-[75vh] ${card}`}>
          <div className={`grid grid-cols-[1.4fr_1.4fr_auto_auto_1fr] gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wider sticky top-0 z-10 ${isDark ? 'bg-gray-900/95 text-gray-500 border-b border-gray-800' : 'bg-gray-50 text-gray-400 border-b border-gray-200'} min-w-[720px]`}>
            <div>Referrer</div><div>Referee</div><div>Status</div><div>Coins</div><div>Signup / Date</div>
          </div>
          {referralsList.items.length === 0 && <p className={`text-center py-10 text-sm ${sub}`}>No referrals yet.</p>}
          {referralsList.items.map((r) => (
            <div key={r.id} className={`grid grid-cols-[1.4fr_1.4fr_auto_auto_1fr] gap-3 px-4 py-3 items-center text-sm border-b min-w-[720px] ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{r.referrer_name ?? '—'}</p>
                <p className={`text-xs truncate ${sub}`}>{r.referrer_email}</p>
              </div>
              <div className="min-w-0">
                <p className="font-medium truncate">{r.referee_name ?? '—'}</p>
                <p className={`text-xs truncate ${sub}`}>{r.referee_email}</p>
              </div>
              <div><span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusPill(r.status)}`}>{r.status}</span></div>
              <div className="text-xs">
                <p>referee +{r.referee_bonus}</p>
                <p>referrer +{r.referrer_bonus}</p>
              </div>
              <div className="text-xs">
                <p className={sub}>{new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                {(r.signup_ip || r.signup_phone) && <p className={sub}>{[r.signup_ip, r.signup_phone].filter(Boolean).join(' · ')}</p>}
              </div>
            </div>
          ))}
          {referralsList.loading && referralsList.items.length > 0 && <p className={`text-center py-4 text-xs ${sub}`}>Loading…</p>}
        </div>
      )}

      {tab === 'coins' && (
        <div className="space-y-4">
          {/* user picker */}
          <div className={`rounded-xl border p-4 ${card}`}>
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-950/40' : 'border-gray-200'}`}>
              <Search size={16} className={sub} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search a customer by name or email"
                className="flex-1 bg-transparent outline-none text-sm" />
            </div>
            {matches.length > 0 && (
              <div className={`mt-2 rounded-lg border divide-y ${isDark ? 'border-gray-800 divide-gray-800' : 'border-gray-200 divide-gray-100'}`}>
                {matches.map((u) => (
                  <button key={u.id} onClick={() => pickUser(u)} className={`w-full text-left px-3 py-2 text-sm ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                    <span className="font-medium">{u.first_name} {u.last_name}</span> <span className={sub}>· {u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && coinsSummary && (
            <div className={`rounded-xl border p-5 ${card}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-bold">{coinsSummary.user.first_name} <span className={`font-normal ${sub}`}>· {coinsSummary.user.email}</span></p>
                  <p className="text-2xl font-extrabold text-emerald-500 mt-1">{coinsSummary.balance} <span className="text-base font-semibold">coins</span></p>
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <label className={`block text-xs mb-1 ${sub}`}>Adjust (± coins)</label>
                    <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^-0-9]/g, ''))} placeholder="e.g. 100 or -50"
                      className={`w-28 px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'border-gray-700 bg-gray-950/40' : 'border-gray-200'}`} />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${sub}`}>Note</label>
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason"
                      className={`w-44 px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'border-gray-700 bg-gray-950/40' : 'border-gray-200'}`} />
                  </div>
                  <button onClick={submitAdjust} className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold">Apply</button>
                </div>
              </div>
              {msg && <p className="text-xs text-emerald-500 mt-2">{msg}</p>}
              {err && <p className="text-xs text-red-500 mt-2">{err}</p>}

              <p className="text-sm font-semibold mt-5 mb-2">Ledger</p>
              <div onScroll={ledger.onScroll} className={`rounded-lg border divide-y max-h-80 overflow-y-auto ${isDark ? 'border-gray-800 divide-gray-800' : 'border-gray-200 divide-gray-100'}`}>
                {ledger.items.length === 0 && <p className={`text-center py-6 text-sm ${sub}`}>No coin activity.</p>}
                {ledger.items.map((h) => (
                  <div key={h.id} className="flex items-center justify-between px-4 py-2 text-sm">
                    <div>
                      <p className="font-medium">{h.note || h.type}{h.status === 'pending' ? ' (pending)' : ''}</p>
                      <p className={`text-xs ${sub}`}>{new Date(h.created_at).toLocaleString('en-IN')}</p>
                    </div>
                    <span className={`font-bold ${h.amount >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{h.amount >= 0 ? '+' : ''}{h.amount}</span>
                  </div>
                ))}
                {ledger.loading && ledger.items.length > 0 && <p className={`text-center py-3 text-xs ${sub}`}>Loading…</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
